<!-- src/components/VoiceMailRecord.vue -->
<template>
  <div class="record-container">
    <!-- Record Header -->
    <div class="record-header" @click="toggleExpanded">
      <div class="arrow-button">{{ isExpanded ? '▾' : '▸' }}</div>
      <div class="header-content">
        <span class="caller-id">{{ prettyPhone(record.callerId) }}</span>
        <span 
         v-if="record.status === 'in-progress' && isRecent(record.timestamp) && record.duration !== 'hangup'" 
         class="status-dot"
        ></span>
        <span v-if="record.flag" class="flag-icon">⚑</span>
        <span v-if="record.language == 'es-MX'" class="language-indicator">
          <span class="desktop-lang">español</span>
          <span class="mobile-lang">ES</span>
        </span>
        <span v-if="record.language == 'ru-RU'" class="language-indicator">
          <span class="desktop-lang">русский</span>
          <span class="mobile-lang">RU</span>
        </span>
        <span class="pretty-seconds" :style="record.duration && record.duration !== 'hangup' && getDurationDisplay(record) !== 'hangup' ? 'color: black; font-weight: bold' : ''">
          {{ getDurationDisplay(record) }}
        </span>
        <span class="timestamp-separator">&nbsp;/&nbsp;</span>
        <span class="timestamp">{{ formatTimestamp(record.timestamp) }}</span>
      </div>
      <button class="kebab-menu" @click.stop="toggleKebabMenu">⋮</button>
      <!-- Kebab menu dropdown -->
      <div v-if="showKebabMenu" class="kebab-menu-dropdown">
        <button @click.stop="archiveRecord">Archive</button>
        <button @click.stop="flagRecord">Flag</button>
        <button @click.stop="unflagRecord">Unflag</button>
      </div>
    </div>

    <!-- Expanded Content -->
    <div v-if="isExpanded" class="expanded-content">

      <!-- DEV TOOLS — env-gated (see DEV_MODE constant). The X button
           switches to real-backend mode for the session: hides the panel
           AND makes the SMS handlers stop using local-state branches.
           Reloading the page brings dev mode back. -->
      <div v-if="devModeActive" class="dev-tools">
        <div class="dev-tools-header">
          <span>🛠 DEV TOOLS</span>
          <button
            class="dev-tools-close"
            title="Switch to real-backend mode for this session (page reload restores)"
            @click="exitDevMode"
          >×</button>
        </div>
        <div class="dev-row">
          <span class="dev-label">Consent:</span>
          <button @click="devSetConsent('pending')">Pending</button>
          <button @click="devSetConsent('granted')">Granted</button>
          <button @click="devSetConsent('declined')">Declined</button>
          <button @click="devSetConsent('revoked')">Revoked</button>
          <button @click="devSetConsent('unknown')">Unknown</button>
        </div>
        <div class="dev-row">
          <span class="dev-label">Add msg:</span>
          <button @click="devAddAutoPrompt">+ Auto-prompt</button>
          <button @click="devAddInbound('yes')">+ YES</button>
          <button @click="devAddInbound('no')">+ NO</button>
          <button @click="devAddInbound('STOP')">+ STOP</button>
          <button @click="devAddInbound('Hi, can someone call me back?')">+ Inbound text</button>
          <button @click="devAddOutbound('Sure, what number is best?')">+ Outbound (agent)</button>
          <button @click="devAddInboundWithImage">+ Inbound w/ image</button>
        </div>
        <div class="dev-row">
          <span class="dev-label">System:</span>
          <button @click="devAddSystem('Consent granted', 'info', '✓')">+ Info</button>
          <button @click="devAddSystem('Consent prompt failed: Twilio rate limit (will retry)', 'warning', '⚠')">+ Warning</button>
          <button @click="devAddSystem('Send failed: invalid recipient', 'error', '⛔')">+ Error</button>
        </div>
        <div class="dev-row">
          <button @click="devResetToFreshVoicemail" class="dev-reset">Reset (pending + auto-prompt)</button>
          <button @click="devResetEmpty" class="dev-reset">Reset (empty + unknown)</button>
        </div>
      </div>

      <!-- SMS thread panel — between header and body -->
      <sms-thread-panel
        ref="smsPanel"
        :thread="smsThread"
        :consent="smsConsent"
        :loading="smsLoading"
        :sending="smsSending"
        :record-language="record.language"
        @unfurl="handleSmsUnfurl"
        @send="handleSmsSend"
        @send-consent-prompt="handleSendConsentPrompt"
        @acknowledge="handleSmsAcknowledge"
      />

      <!-- Audio Player Section -->
      <div class="audio-section">
        <div class="audio-container">
          <audio
            :src="record.recordingUrl"
            controls
            class="audio-player"
            @play="isPlaying = true"
            @pause="isPlaying = false"
          >
            Your browser does not support the audio element.
          </audio>
        </div>
        <div class="duration-timestamp-container">
          <div class="timestamp-section">
            {{ formatDetailedTimestamp(record.timestamp) }}
          </div>
          <div class="duration">
            <span class="duration-label"></span>{{ formatDuration(record.duration) }}
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="summary-section">
        <h3>Summary</h3>
        <p>{{ record.summary }}</p>
      </div>

      <!-- Transcript Section -->
      <div class="transcript-section">
        <div class="transcript-header">
          <h3>Transcript</h3>
          <button 
            class="toggle-transcript"
            @click="showFullTranscript = !showFullTranscript"
          >
            {{ showFullTranscript ? 'Show Less' : 'Show Full Transcript' }}
          </button>
        </div>
        <div 
          class="transcript-content"
          :class="{ 'transcript-collapsed': !showFullTranscript }"
        >
          <div class="transcript-text">{{ record.transcription }}</div>
        </div>
      </div>

      <!-- Notes Section -->
      <div class="notes-section">
        <div class="notes-header">
          <h3>Notes</h3>
          <span v-if="record.notesTimestamp" class="notes-timestamp">
            {{ formatTimestamp(record.notesTimestamp) }}
          </span>
        </div>
        
        <!-- Notes Edit Mode -->
        <div v-if="isEditing" class="notes-edit-container">
          <textarea
            v-model="editingText"
            class="note-edit-textarea"
            rows="3"
            @keyup.esc="cancelEdit"
            ref="editTextarea"
          ></textarea>
          <div class="note-edit-actions">
            <button class="edit-action-button save" @click="saveNotes">
              Save
            </button>
            <button class="edit-action-button cancel" @click="cancelEdit">
              Cancel
            </button>
          </div>
        </div>

        <!-- Notes View Mode -->
        <div v-else class="notes-view-container">
          <div class="notes-text-container">
            <div class="notes-text">{{ record.notes || 'No notes added' }}</div>
            <button class="edit-button" @click="startEdit">✎</button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { prettyPhone, getDurationDisplay } from '../utils/helpers';
import axios from 'axios';
import SmsThreadPanel from './SmsThreadPanel.vue';
import { eventService } from '../services/EventService';

// Dev tools visibility is gated on two env vars to prevent accidental
// production exposure:
//   VUE_APP_THIS_ENVIRONMENT must NOT be 'production'
//   VUE_APP_ENABLE_SMS_DEV_TOOLS must equal the string 'true'
//
// In dev/staging builds, set both in the build env (e.g. .env.development)
// to enable the dev tools panel. Even when the panel is enabled by env,
// the agent can dismiss it for the session via the X button — see
// _devModeOff below. Reloading the page brings it back.
const DEV_MODE = process.env.VUE_APP_THIS_ENVIRONMENT !== 'production'
              && process.env.VUE_APP_ENABLE_SMS_DEV_TOOLS === 'true';

// Module-level reactive state — shared across ALL VoiceMailRecord
// instances on the page. When the agent clicks X on any record's
// dev-tools panel, dev mode flips off for every record for the
// rest of the session: the dev tools hide AND the SMS handlers stop
// using their local-state branches and start hitting the real backend.
// Page reload re-imports the module, resetting this back to false (so
// dev mode comes back, assuming env vars still allow it).
const _devModeOff = ref(false);

// Helper for dev mode: time a few seconds in the past, so successive
// dev-added messages don't all share a timestamp.
let _devClockOffset = 0;
function devNow() {
  _devClockOffset += 1;
  return new Date(Date.now() - 1000 * (60 - _devClockOffset));
}

export default {
 name: 'VoiceMailRecord',

 components: { SmsThreadPanel },
 
 props: {
   record: {
     type: Object,
     required: true
   }
 },

 data() {
   return {
     isExpanded: false,
     isPlaying: false,
     showFullTranscript: false,
     isEditing: false,
     editingText: '',
     showKebabMenu: false,

     // SMS thread panel state
     smsLoading: false,
     smsSending: false,
     smsThread: [],
     smsConsent: { status: 'unknown', statusAt: null },
     smsThreadLoaded: false,  // gates lazy load
   }
 },

 computed: {
   // Live "is dev mode active?" check. Both must be true:
   //   DEV_MODE — env vars allow dev tools at all (compile-time constant)
   //   !_devModeOff.value — agent hasn't dismissed dev mode this session
   //
   // Used both for showing the dev-tools panel AND for routing handler
   // bodies through their dev branches. So when the agent clicks X mid-
   // session, dev tools hide AND the panel switches to real-backend mode
   // simultaneously across every record on the page.
   devModeActive() {
     return DEV_MODE && !_devModeOff.value;
   },
 },

 watch: {
   // When dev mode flips off (typically because another VoiceMailRecord's
   // X button mutated the shared ref), this record needs to subscribe to
   // SSE — it didn't at mount because devModeActive was true.
   //
   // Only false→true transitions matter; we don't unsubscribe when going
   // back the other way (which can't happen mid-session anyway since
   // _devModeOff only goes one direction until page reload). The
   // subscribe method is idempotent so it's safe even if called twice.
   devModeActive(active, wasActive) {
     if (wasActive && !active) {
       this.subscribeToSmsEvents();
     }
   },
 },

 methods: {
   prettyPhone,
   getDurationDisplay,

   isRecent(timestamp) {
     const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
     return new Date(timestamp) > fiveMinutesAgo;
   },

   toggleExpanded() {
     this.isExpanded = !this.isExpanded;
   },

   toggleKebabMenu(event) {
     document.dispatchEvent(new Event('close-all-kebabs'));
     this.showKebabMenu = !this.showKebabMenu;
     event.stopPropagation();
   },

   handleOutsideClick(event) {
     if (this.showKebabMenu && 
         !event.target.closest('.kebab-menu-dropdown') && 
         !event.target.closest('.kebab-menu')) {
       this.showKebabMenu = false;
     }
   },

   handleEscKey(event) {
     if (event.key === 'Escape' && this.showKebabMenu) {
       this.showKebabMenu = false;
     }
   },

   async archiveRecord() {
    try {
      const updateObject = {
        status: 'archived'
      };
      
      await axios.put(`/portaldb/db/updateVoicemailPropertiesWithCompany/${this.record.callSid}/${this.record.companyNumber}`, updateObject);
      
      // Emit update to parent with updated record
      this.$emit('update:record', {
        ...this.record,
        ...updateObject
      });
      
      this.showKebabMenu = false;
    } catch (error) {
      console.error('Error archiving record:', error);
    }
   },

    // In VoiceMailRecord.vue methods:
    async flagRecord() {
      try {
        const updateObject = {
          flag: true
        };
        
        await axios.put(`/portaldb/db/updateVoicemailProperties/${this.record.callSid}`, updateObject);
        
        // Emit update to parent with updated record
        this.$emit('update:record', {
          ...this.record,
          ...updateObject
        });
        
        this.showKebabMenu = false;
      } catch (error) {
        console.error('Error flagging record:', error);
        // You might want to add error handling UI here
      }
    },

    async unflagRecord() {
      try {
        const updateObject = {
          flag: false
        };
        
        await axios.put(`/portaldb/db/updateVoicemailProperties/${this.record.callSid}`, updateObject);
        
        // Emit update to parent with updated record
        this.$emit('update:record', {
          ...this.record,
          ...updateObject
        });
        
        this.showKebabMenu = false;
      } catch (error) {
        console.error('Error unflagging record:', error);
        // You might want to add error handling UI here
      }
    },

   startEdit() {
     this.isEditing = true;
     this.editingText = this.record.notes || '';
     this.$nextTick(() => {
       this.$refs.editTextarea.focus();
     });
   },

   async saveNotes() {
     try {
       const updateObject = {
         notes: this.editingText,
         notesTimestamp: Date.now()
       };

       await axios.put(`/portaldb/db/updateVoicemailProperties/${this.record.callSid}`, updateObject);

       // Emit update to parent with updated record
       this.$emit('update:record', {
         ...this.record,
         ...updateObject
       });

       this.isEditing = false;
       this.editingText = '';
     } catch (error) {
       console.error('Error saving notes:', error);
       // You might want to add error handling UI here
     }
   },

   cancelEdit() {
     this.isEditing = false;
     this.editingText = '';
   },

   // -------------------------------------------------------------
   // SMS thread panel handlers
   // -------------------------------------------------------------
   //
   // Each handler has a devModeActive branch (local-state mutation, no
   // network) and a real-backend branch (axios calls to portalAPI).
   // Both produce equivalent UI behavior. devModeActive is true when
   // env vars enable dev tools AND the agent hasn't clicked the X
   // button to switch to real-backend mode for the session.

   async handleSmsUnfurl() {
     if (this.smsThreadLoaded || this.smsLoading) return;
     this.smsLoading = true;
     try {
       if (this.devModeActive) {
         // Thread is whatever the dev-tools buttons set; just flip the flag.
         await new Promise(r => setTimeout(r, 200)); // simulate latency
       } else {
         const phone = this.record.callerId;
         const cn = this.record.companyNumber;
         const { data } = await axios.get('/portaldb/db/sms/thread', {
           params: { phone, companyNumber: cn },
         });
         if (data && data.success) {
           this.smsThread = data.thread || [];
           this.smsConsent = data.consent || { status: 'unknown', statusAt: null };
         } else {
           console.warn('[VoiceMailRecord] sms/thread returned non-success:', data);
         }
       }
       this.smsThreadLoaded = true;
     } catch (err) {
       console.error('[VoiceMailRecord] failed to load SMS thread:', err);
     } finally {
       this.smsLoading = false;
     }
   },

   async handleSmsSend({ body, attachment }) {
     this.smsSending = true;
     try {
       if (this.devModeActive) {
         // Append optimistically; in production the SSE pushback is the
         // source of truth, but in dev we have no SSE so we mutate locally.
         await new Promise(r => setTimeout(r, 300)); // simulate latency
         const mediaUrls = [];
         if (attachment) {
           const url = URL.createObjectURL(attachment);
           mediaUrls.push({ url, contentType: attachment.type });
         }
         this.smsThread.push({
           id: 'dev_' + Date.now(),
           type: 'outbound',
           body,
           sentBy: 'eric@aabb.example',
           at: new Date(),
           mediaUrls,
         });
         this.$refs.smsPanel?.clearComposer();
       } else {
         const phone = this.record.callerId;
         const cn = this.record.companyNumber;
         const formData = new FormData();
         formData.append('to', phone);
         formData.append('companyNumber', String(cn));
         if (body) formData.append('body', body);
         if (attachment) formData.append('image', attachment);

         const { data } = await axios.post('/portaldb/db/sms/send', formData, {
           headers: { 'Content-Type': 'multipart/form-data' },
         });

         if (data && data.success) {
           // Outbound message will arrive via SSE; we don't append locally
           // here to avoid a double-render once SSE delivers the same record.
           this.$refs.smsPanel?.clearComposer();
         } else {
           // Send was blocked by SmsManager — surface the reason. Common
           // reasons: consent_required, consent_revoked, twilio_error,
           // bad_recipient, bad_body, config_error.
           const reason = (data && (data.reason || data.message)) || 'unknown';
           console.warn('[VoiceMailRecord] SMS send blocked:', reason, data);
           // TODO: surface to user via toast/dialog. For now, log only —
           // the agent will wonder why nothing happened. Acceptable for
           // dev/test; revisit when polishing UX.
         }
       }
     } catch (err) {
       console.error('[VoiceMailRecord] SMS send failed:', err);
     } finally {
       this.smsSending = false;
     }
   },

   async handleSendConsentPrompt(payload = {}) {
     try {
       if (this.devModeActive) {
         this.devAddAutoPrompt();
         this.smsConsent = { status: 'pending', statusAt: null };
       } else {
         const phone = this.record.callerId;
         const cn = this.record.companyNumber;
         // Prefer the voicemail's detected language. The voicemail
         // detector emits BCP-47 codes that already match SmsManager's
         // template keys (en-US, es-MX, ru-RU). For anything outside
         // that whitelist, fall back to en-US client-side so the prompt
         // still goes out — better than a confusing 400.
         //
         // The caller can override via payload.language — used by the
         // pending-state "Resend in English" button.
         const SUPPORTED = ['en-US', 'es-MX', 'ru-RU'];
         const detected = this.record.language;
         const language = payload.language
           || (SUPPORTED.includes(detected) ? detected : 'en-US');
         const { data } = await axios.post('/portaldb/db/sms/sendConsentPrompt', {
           to: phone,
           companyNumber: cn,
           language,
         });
         if (data && data.success) {
           // Pending consent record + prompt-as-outbound-message will both
           // arrive via SSE; no local mutation needed.
         } else {
           // Most common non-success reason here is already_pending /
           // already_granted / already_declined / consent_revoked — all
           // expected idempotency outcomes from SmsManager.
           const reason = (data && (data.reason || data.message)) || 'unknown';
           console.warn('[VoiceMailRecord] sendConsentPrompt blocked:', reason, data);
         }
       }
     } catch (err) {
       console.error('[VoiceMailRecord] sendConsentPrompt failed:', err);
     }
   },

   async handleSmsAcknowledge() {
     // Optimistic local update first — flip every unacked inbound to
     // acknowledged immediately, before the network round trip.
     this.smsThread = this.smsThread.map((m) =>
       m.type === 'inbound' && !m.acknowledged
         ? { ...m, acknowledged: true }
         : m
     );
     if (this.devModeActive) return;
     try {
       const phone = this.record.callerId;
       const cn = this.record.companyNumber;
       await axios.post('/portaldb/db/sms/acknowledge', {
         phone,
         companyNumber: cn,
       });
     } catch (err) {
       console.error('[VoiceMailRecord] ack failed:', err);
       // Don't roll back the optimistic update — best-effort. The next
       // GET /thread will reflect actual server state.
     }
   },


   // -------------------------------------------------------------
   // SSE — live updates for SMS messages and consent state changes
   // -------------------------------------------------------------
   //
   // Each VoiceMailRecord subscribes to two event types on the company's
   // SSE connection: 'smsMessages' (inbound or outbound message inserted)
   // and 'smsConsent' (consent state inserted or updated). The handlers
   // filter by phoneNumber to act only on events for THIS record's caller.
   //
   // Subscription happens in mounted() regardless of unfurl state, because
   // the consent pill in the header reflects state even when the panel is
   // collapsed. Thread mutations are gated on smsThreadLoaded so we don't
   // partially populate a thread that hasn't been loaded yet — the next
   // unfurl's GET will return the full state.
   //
   // Bound handlers are stored on the instance so removeEventListener can
   // pass the same reference back. Without this, unsubscribe is a no-op
   // and the listener leaks on component teardown.

   subscribeToSmsEvents() {
     // Idempotent — if already subscribed, do nothing. Reasons this can
     // be called more than once: mounted() fires once, then exitDevMode()
     // fires when the agent dismisses dev mode mid-session.
     if (this._boundSmsMessageHandler) return;
     if (this.devModeActive) return;
     const cn = this.record?.companyNumber;
     if (!cn) return;
     const events = eventService.getConnection(cn);
     if (!events) return;

     this._boundSmsMessageHandler = (event) => this.handleSmsMessageEvent(event);
     this._boundSmsConsentHandler = (event) => this.handleSmsConsentEvent(event);
     events.addEventListener('smsMessages', this._boundSmsMessageHandler);
     events.addEventListener('smsConsent', this._boundSmsConsentHandler);
   },

   unsubscribeFromSmsEvents() {
     // Decide whether to unsubscribe based on whether handlers were
     // actually registered, not on current dev-mode state. The user may
     // have toggled dev mode mid-session; we always want to clean up
     // whatever listeners we have.
     if (!this._boundSmsMessageHandler && !this._boundSmsConsentHandler) return;
     const cn = this.record?.companyNumber;
     if (!cn) return;
     const events = eventService.getConnection(cn);
     if (!events) return;
     if (this._boundSmsMessageHandler) {
       events.removeEventListener('smsMessages', this._boundSmsMessageHandler);
       this._boundSmsMessageHandler = null;
     }
     if (this._boundSmsConsentHandler) {
       events.removeEventListener('smsConsent', this._boundSmsConsentHandler);
       this._boundSmsConsentHandler = null;
     }
   },

   handleSmsMessageEvent(event) {
     // Only mutate the thread once it's been loaded — otherwise we'd
     // create an orphan partial thread that the unfurl GET would later
     // overwrite (or duplicate from). Header-only state (none currently)
     // could be updated even when not loaded; for now there's nothing
     // here that needs to fire when the panel is collapsed.
     if (!this.smsThreadLoaded) return;

     let msg;
     try { msg = JSON.parse(event.data); }
     catch (e) { return; }

     // Filter to this record's caller. Three matchable shapes:
     //   inbound  — msg.from === my phone
     //   outbound — msg.to   === my phone
     //   system   — msg.related_to === my phone (consent transitions or
     //              send failures, written by SmsManager and the route
     //              handlers respectively)
     const myPhone = this.record.callerId;
     const isInbound  = msg.direction === 'inbound'  && msg.from        === myPhone;
     const isOutbound = msg.direction === 'outbound' && msg.to          === myPhone;
     const isSystem   = msg.direction === 'system'   && msg.related_to  === myPhone;
     if (!isInbound && !isOutbound && !isSystem) return;

     // De-dup: SSE may deliver the same record twice on update events,
     // and the GET response may include a record that arrived between
     // mount and first unfurl.
     if (this.smsThread.find((m) => m.id === msg.messageSid)) return;

     if (isSystem) {
       this.smsThread.push({
         id: msg.messageSid,
         type: 'system',
         body: msg.body || '',
         severity: msg.severity || 'info',
         reason: msg.reason || null,
         attemptedBy: msg.attempted_by || null,
         at: msg.sentAt || null,
       });
     } else {
       const at = (msg.direction === 'inbound' ? msg.receivedAt : msg.sentAt)
                || msg.receivedTimestamp
                || null;
       this.smsThread.push({
         id: msg.messageSid,
         type: msg.direction === 'inbound' ? 'inbound' : 'outbound',
         body: msg.body || '',
         at,
         sentBy: msg.sent_by || null,
         mediaUrls: Array.isArray(msg.mediaUrls) ? msg.mediaUrls : [],
         acknowledged: !!msg.acknowledged_at,
       });
     }
     // Keep chronological ordering (events can arrive out of order).
     this.smsThread.sort((a, b) => new Date(a.at) - new Date(b.at));
   },

   handleSmsConsentEvent(event) {
     let consentRec;
     try { consentRec = JSON.parse(event.data); }
     catch (e) { return; }

     // Filter to this record's caller.
     if (consentRec.phoneNumber !== this.record.callerId) return;

     // Update the consent state — header pill reflects this even when the
     // panel is collapsed. We do NOT synthesize system notes here anymore;
     // SmsManager writes real direction='system' rows into smsMessages on
     // each consent transition, and they arrive via the regular smsMessages
     // SSE channel + handleSmsMessageEvent.
     this.smsConsent = {
       status: consentRec.status,
       statusAt: consentRec.grantedAt
              || consentRec.revokedAt
              || consentRec.updatedAt
              || null,
     };
   },


   // -------------------------------------------------------------
   // DEV helpers — used by the dev-tools buttons. The whole block
   // (including the dev-tools <div> in template and styles below)
   // can be deleted when you no longer want this affordance.
   // -------------------------------------------------------------

   exitDevMode() {
     // Mutates the module-level ref, which all VoiceMailRecord instances
     // share — so dev mode flips off everywhere immediately. Dev tools
     // hide AND every handler's dev-branch goes inactive at the same time.
     //
     // Subscribe to SSE right now: subscribeToSmsEvents() was a no-op at
     // mount because devModeActive was true; without subscribing here,
     // this record would miss live updates until the next page load.
     // The other VoiceMailRecord instances will subscribe via their own
     // watchers below — see the watch on devModeActive.
     _devModeOff.value = true;
     this.subscribeToSmsEvents();
   },

   devSetConsent(status) {
     const statusAt = (status === 'granted' || status === 'declined' || status === 'revoked')
       ? new Date()
       : null;
     this.smsConsent = { status, statusAt };
   },

   devAddAutoPrompt() {
     this.smsThread.push({
       id: 'dev_' + Date.now(),
       type: 'outbound',
       body: 'This is A-Affordable Bail Bonds. We received your voicemail. Reply YES if we can text you when an agent is available. STOP to opt out.',
       sentBy: 'system',
       at: devNow(),
     });
   },

   devAddInbound(body) {
     this.smsThread.push({
       id: 'dev_' + Date.now(),
       type: 'inbound',
       body,
       at: devNow(),
       acknowledged: false,
     });
   },

   devAddInboundWithImage() {
     // Use a small placeholder image (data URL) so the dev case works offline
     this.smsThread.push({
       id: 'dev_' + Date.now(),
       type: 'inbound',
       body: 'Here\'s the form',
       at: devNow(),
       acknowledged: false,
       mediaUrls: [{
         url: 'https://via.placeholder.com/300x200/d2b48c/5c4633?text=customer+photo',
         contentType: 'image/png',
       }],
     });
   },

   devAddOutbound(body) {
     this.smsThread.push({
       id: 'dev_' + Date.now(),
       type: 'outbound',
       body,
       sentBy: 'eric@aabb.example',
       at: devNow(),
     });
   },

   devAddSystem(body, severity, icon) {
     this.smsThread.push({
       id: 'dev_' + Date.now(),
       type: 'system',
       body,
       severity,
       icon,
       at: devNow(),
     });
   },

   devResetToFreshVoicemail() {
     _devClockOffset = 0;
     this.smsThread = [];
     this.smsConsent = { status: 'pending', statusAt: null };
     this.devAddAutoPrompt();
   },

   devResetEmpty() {
     _devClockOffset = 0;
     this.smsThread = [];
     this.smsConsent = { status: 'unknown', statusAt: null };
   },


   formatDuration(seconds) {
     const minutes = Math.floor(seconds / 60);
     const remainingSeconds = seconds % 60;
     return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
   },

   formatTimestamp(timestamp) {
     const diffInSeconds = (Date.now() - new Date(timestamp)) / 1000;
     if (diffInSeconds < 60) {
       return 'just now';
     } else if (diffInSeconds < 3600) {
       const minutes = Math.floor(diffInSeconds / 60);
       return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`;
     } else if (diffInSeconds < 86400) {
       const hours = Math.floor(diffInSeconds / 3600);
       return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
     } else if (diffInSeconds < 172800) {
       return 'yesterday';
     } else {
       const days = Math.floor(diffInSeconds / 86400);
       return `${days} days ago`;
     }
   },

   formatDetailedTimestamp(timestamp) {
     return new Date(timestamp).toLocaleString('en-US', {
       year: 'numeric',
       month: 'short',
       day: 'numeric',
       hour: '2-digit',
       minute: '2-digit'
     });
   }
 },

 mounted() {
   document.addEventListener('click', this.handleOutsideClick);
   document.addEventListener('keydown', this.handleEscKey);
   document.addEventListener('close-all-kebabs', () => this.showKebabMenu = false);
   this.subscribeToSmsEvents();
 },

 beforeUnmount() {
   document.removeEventListener('click', this.handleOutsideClick);
   document.removeEventListener('keydown', this.handleEscKey);
   document.removeEventListener('close-all-kebabs', () => this.showKebabMenu = false);
   this.unsubscribeFromSmsEvents();
 }
};
</script>

<style scoped>

.status-dot {
  width: 8px;
  height: 8px;
  background-color: #cc0000;
  border-radius: 50%;
  display: inline-block;
  margin: 0 8px;
}

.language-indicator {
  color: #6495ED;
  margin-right: 10px;
  font-style: italic;
}

.mobile-lang {
  display: none;
}

.flag-icon {
  color: #dc3545;
  font-size: 24px;
  margin: 0 8px;
  display: flex;
  align-items: center;
}

.record-container {
  border: 1px solid #d2b48c;
  margin-bottom: 10px;
  border-radius: 4px;
}

.record-header {
  background-color: #f5f5dc;
  padding: 0px;
  padding-left:4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
}

.header-content {
  display: flex;
  flex-grow: 1;
  align-items: center;
  margin-right: 8px;
}

.arrow-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #8b4513;
  font-size: 36px;
  margin-right: 4px;
  display: flex;
  align-items: center;
  padding: 0;
  background-color: transparent !important; 
  -webkit-appearance: none; 
  -moz-appearance: none;
  appearance: none;
  outline: none; 
}

.arrow-button:focus-visible {
  outline: 2px solid #8b4513;
  outline-offset: 2px;
}

.arrow-button:active,
.arrow-button:focus {
  color: #8b4513;
  background-color: transparent;
}

.caller-id {
  flex-grow: 1;
  font-weight: 500;
}

.expanded-content {
  padding: 1rem;
  background-color: white;
  padding-top: 0px;
}

.audio-section {
  display: flex;
  align-items: center;
  gap: 2rem;
  background-color: #f8f8f0;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
}

.audio-container {
  flex: 0 0 60%;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.audio-player {
  width: 100%;
  height: 32px;
}

.duration {
  font-size: 0.875rem;
  color: #666;
  white-space: nowrap;
}

.timestamp-section {
  flex: 1;
  text-align: right;
  color: #666;
  font-size: 0.875rem;
}

.summary-section,
.transcript-section,
.notes-section {
  border: 1px solid #d2b48c;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.summary-section h3,
.transcript-section h3,
.notes-section h3 {
  font-weight: 500;
  margin: 0 0 0.5rem 0;
}

.transcript-header,
.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.toggle-transcript {
  color: #8b4513;
  font-size: 0.875rem;
  background: none;
  border: none;
  cursor: pointer;
}

.toggle-transcript:hover {
  text-decoration: underline;
}

.transcript-content {
  line-height: 1.5;
  max-height: none;
  opacity: 1;
}

.transcript-collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: 4.5em;
  opacity: 0.8;
}

.transcript-text,
.notes-text {
  line-height: 1.6;
}

.notes-timestamp {
  color: #666;
  font-size: 0.875rem;
}

.notes-text-container {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 4px;
}

.edit-button {
  background: none;
  border: none;
  font-size: 16px;
  color: #8b4513;
  cursor: pointer;
  opacity: 0.5;
  padding: 4px;
  transition: opacity 0.2s ease;
}

.edit-button:hover {
  opacity: 1;
}

.note-edit-textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;
  margin-bottom: 8px;
}

.note-edit-textarea:focus {
  outline: none;
  border-color: #8b4513;
  box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
}

.note-edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.edit-action-button {
  padding: 4px 12px;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.edit-action-button.save {
  background-color: #d2b48c;
  color: white;
}

.edit-action-button.save:hover {
  background-color: #8b4513;
  border-color: #8b4513;
}

.edit-action-button.cancel {
  background-color: white;
  color: #666;
}

.edit-action-button.cancel:hover {
  background-color: #f5f5f5;
}

.kebab-menu {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #8b4513;
  margin-left: 6px;
  padding: 8px 12px;
  outline: none;
  display: flex;
  align-items: center;
  height: 100%;
}

.kebab-menu:focus {
  outline: none;
}

.kebab-menu-dropdown {
  position: absolute;
  background-color: white;
  border: 1px solid #d2b48c;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 5px 10px;
  z-index: 100;
  right: 0;
  top: 100%;
}

.kebab-menu-dropdown button {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
}

.kebab-menu-dropdown button:hover {
  background-color: #f5f5dc;
}

.duration-label:before {
  content: "Duration: ";
}

.timestamp, .pretty-seconds {
  color: gray;
}

@media (max-width: 600px) {

  .timestamp-separator {
    display: none;
  }
  
  .status-dot {
    margin: 0 6px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    position: relative;
  }

  .caller-id {
    flex-grow: 0;
    padding-left: 32px;  /* Make space for the flag */
  }

  .flag-icon {
    color: #dc3545;
    font-size: 20px;
    margin: 0;
    display: flex;
    align-items: center;
    position: absolute;
    right: -38px;  /* Increased from 28px to 48px */
    top: -12px;   /* Changed from -3px to -4px */
  }

  .language-indicator {
    display: inline-flex;
    margin: 0;
  }

  .timestamp {
    margin-top: 4px;
    font-size: 0.9em;
    display: inline-flex;
  }

  .record-header {
    align-items: center;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .arrow-button {
    margin-top: 4px;
  }

  .audio-section {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }

  .audio-container {
    display: block;
    width: 100%;
  }

  .audio-player {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .duration-timestamp-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding-top: 0.5rem;
  }

  .timestamp-section {
    text-align: left;
  }

  .duration {
    text-align: right;
  }

  .duration-label:before {
    content: "Dur: ";
  }

  .desktop-lang {
    display: none;
  }
  
  .mobile-lang {
    display: inline;
  }
}

/* ------------------------------------------------------------ */
/* DEV TOOLS — remove with the dev-tools <div> when shipping.    */
/* ------------------------------------------------------------ */

.dev-tools {
  background-color: #fff8dc;
  border: 2px dashed #d2691e;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;
  font-size: 12px;
}

.dev-tools-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  color: #d2691e;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}

.dev-tools-close {
  background: none;
  border: none;
  color: #d2691e;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 4px;
  font-weight: bold;
}

.dev-tools-close:hover {
  color: #8b3500;
}

.dev-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 4px 0;
}

.dev-label {
  font-weight: bold;
  color: #5c4633;
  margin-right: 4px;
  min-width: 65px;
}

.dev-row button {
  font-size: 11px;
  padding: 3px 8px;
  border: 1px solid #d2691e;
  background-color: #fff;
  color: #5c4633;
  border-radius: 3px;
  cursor: pointer;
}

.dev-row button:hover {
  background-color: #ffeac4;
}

.dev-row button.dev-reset {
  border-color: #b87a00;
  background-color: #fff8dc;
  font-weight: bold;
}

.dev-row button.dev-reset:hover {
  background-color: #ffeac4;
}

</style>
